import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { RITMO_LABEL, type RitmoStatus } from "@brasilnovo/shared";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { Card, ErrorBox, Kpi, PageHeader, Spinner, Table, num, pct } from "../components/ui.js";

interface MetaRow {
  regiao: string;
  competencia: string;
  liberado: number | null;
  agendado: number | null;
  carregado: number | null;
  toneladas_disponiveis: number | null;
  toneladas_agendadas: number | null;
  toneladas_realizadas: number | null;
  realizado: number;
  total_cargas: number | null;
  peso_total: number | null;
  meta_cargas: number | null;
  meta_peso: number | null;
  meta_diaria_toneladas: number | null;
  cargas_agendadas_hoje: number | null;
  toneladas_agendadas_hoje: number | null;
  cargas_realizadas_hoje: number | null;
  toneladas_realizadas_hoje: number | null;
  ritmo_dia: number | null;
  veiculos_faltantes_40t: number | null;
  cargas_restantes: number | null;
  pct_atendida: number | null;
  pct_ritmo: number | null;
  ritmo_status: RitmoStatus;
  veiculos_necessarios: number | null;
}

const RITMO_CLS: Record<RitmoStatus, string> = {
  adiantado: "bg-green-100 text-green-700",
  no_prazo: "bg-blue-100 text-blue-700",
  atrasado: "bg-red-100 text-red-700",
  nao_iniciado: "bg-slate-100 text-slate-600",
  sem_meta: "bg-slate-100 text-slate-400",
};

const shortRegiao = (r: string) => r.replace(/^\d+_REGIÃO_/, "");

function RitmoBadge({ status }: { status: RitmoStatus }) {
  return <span className={`badge ${RITMO_CLS[status]}`}>{RITMO_LABEL[status]}</span>;
}

// Barra de progresso da % atendida (verde ≥100, azul ≥80, âmbar <80).
function AttainmentBar({ frac }: { frac: number | null }) {
  if (frac == null) return <span className="text-slate-400">—</span>;
  const p = Math.max(0, Math.min(frac, 1.2));
  const color = frac >= 1 ? "bg-green-500" : frac >= 0.8 ? "bg-blue-500" : "bg-amber-500";
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full ${color}`} style={{ width: `${(p / 1.2) * 100}%` }} />
      </div>
      <span className="tabular-nums text-slate-700">{pct(frac)}</span>
    </div>
  );
}

export function Metas() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const canEditMeta = can("metas.editar");

  const comps = useQuery({ queryKey: ["metas-competencias"], queryFn: async () => (await api.get("/metas/competencias")).data as string[] });
  const [comp, setComp] = useState<string>("");
  const [metaTodosCargas, setMetaTodosCargas] = useState("");
  const [metaTodosPeso, setMetaTodosPeso] = useState("");
  const [bulkSuccess, setBulkSuccess] = useState("");
  const competencia = comp || comps.data?.[0] || "";
  const ano = competencia ? Number(competencia.slice(0, 4)) : undefined;
  const mes = competencia ? Number(competencia.slice(5, 7)) : undefined;

  const metas = useQuery({
    queryKey: ["metas", ano, mes],
    enabled: !!competencia,
    queryFn: async () => (await api.get(`/metas?ano=${ano}&mes=${mes}`)).data as MetaRow[],
  });
  const rows = metas.data ?? [];

  const saveMeta = useMutation({
    mutationFn: async (v: { regiao: string; meta_cargas?: number | null; meta_peso?: number | null }) =>
      (await api.put("/metas/alvo", { competencia: competencia.slice(0, 7), ...v })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["metas", ano, mes] }),
  });

  const saveMetaTodos = useMutation({
    mutationFn: async () => {
      const payload: {
        competencia: string;
        meta_cargas?: number;
        meta_peso?: number;
      } = { competencia: competencia.slice(0, 7) };
      if (metaTodosCargas !== "") payload.meta_cargas = Number(metaTodosCargas);
      if (metaTodosPeso !== "") payload.meta_peso = Number(metaTodosPeso);
      return (await api.put("/metas/alvo/todos", payload)).data as { updated: number };
    },
    onMutate: () => setBulkSuccess(""),
    onSuccess: async (data) => {
      await qc.invalidateQueries({ queryKey: ["metas", ano, mes] });
      setBulkSuccess(`Meta atribuída a ${data.updated} regiões.`);
      setMetaTodosCargas("");
      setMetaTodosPeso("");
    },
  });

  // KPIs agregados da competência.
  const kpis = useMemo(() => {
    const comMeta = rows.filter((r) => r.meta_cargas != null);
    const metaTotal = comMeta.reduce((a, r) => a + (r.meta_cargas ?? 0), 0);
    const realizadoTotal = comMeta.reduce((a, r) => a + r.realizado, 0);
    const veiculos = comMeta.reduce((a, r) => a + (r.veiculos_necessarios ?? 0), 0);
    const atrasadas = comMeta.filter((r) => r.ritmo_status === "atrasado").length;
    const toneladasRealizadas = rows.reduce((a, r) => a + Number(r.toneladas_realizadas ?? 0), 0);
    const toneladasAgendadasHoje = rows.reduce((a, r) => a + Number(r.toneladas_agendadas_hoje ?? 0), 0);
    const veiculos40t = rows.reduce((a, r) => a + Number(r.veiculos_faltantes_40t ?? 0), 0);
    return {
      metaTotal, realizadoTotal, veiculos, atrasadas, toneladasRealizadas, toneladasAgendadasHoje, veiculos40t,
      pctGlobal: metaTotal > 0 ? realizadoTotal / metaTotal : null,
      comMeta: comMeta.length,
    };
  }, [rows]);

  const chartData = rows.slice(0, 12).map((m) => ({
    regiao: shortRegiao(m.regiao),
    Realizado: m.realizado,
    Meta: m.meta_cargas ?? 0,
  }));

  return (
    <div>
      <PageHeader
        title="Metas por região"
        subtitle="Alvo × realizado por competência — % atendida, ritmo e necessidade de veículos"
        action={
          <select className="input w-40" value={competencia} onChange={(e) => setComp(e.target.value)}>
            {(comps.data ?? []).map((c) => (
              <option key={c} value={c}>{c.slice(0, 7)}</option>
            ))}
          </select>
        }
      />

      {metas.isLoading ? <Spinner /> : metas.isError ? <ErrorBox error={metas.error} /> : (
        <>
          {/* ── Dashboard (acima) ── */}
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi label="Meta (cargas)" value={num(kpis.metaTotal)} hint={`${kpis.comMeta} regiões com meta`} />
            <Kpi label="Realizado" value={num(kpis.realizadoTotal)} hint="cargas carregadas" />
            <Kpi label="% atendida" value={pct(kpis.pctGlobal)} hint="realizado ÷ meta" />
            <Kpi label="Regiões atrasadas" value={num(kpis.atrasadas)} hint="abaixo do ritmo" />
            <Kpi label="Toneladas realizadas" value={num(kpis.toneladasRealizadas)} hint="cargas carregadas no mês" />
            <Kpi label="Agendado hoje (t)" value={num(kpis.toneladasAgendadasHoje)} hint="coleta prevista para hoje" />
            <Kpi label="Veículos faltantes" value={num(kpis.veiculos40t)} hint="premissa de 40 t/veículo" />
            <Kpi label="Capacidade diária" value={num(kpis.veiculos)} hint="veículos/dia pelo prazo restante" />
          </div>

          <Card className="mb-6">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Realizado × Meta por região</div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="regiao" fontSize={10} angle={-20} textAnchor="end" height={60} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Realizado" fill="#22c55e" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Meta" fill="#94a3b8" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {canEditMeta && (
            <Card className="mb-6">
              <div className="mb-3">
                <div className="text-sm font-semibold text-slate-800">Atribuir para todos</div>
                <div className="text-xs text-slate-500">
                  Aplique a mesma meta a todas as regiões de {competencia.slice(0, 7)}. Campos em branco não alteram os valores atuais.
                </div>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <label className="text-xs font-medium text-slate-600">
                  Meta (cargas)
                  <input
                    type="number"
                    min={0}
                    value={metaTodosCargas}
                    onChange={(e) => setMetaTodosCargas(e.target.value)}
                    className="input mt-1 block w-36"
                    data-testid="meta-todos-cargas"
                  />
                </label>
                <label className="text-xs font-medium text-slate-600">
                  Meta (t)
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={metaTodosPeso}
                    onChange={(e) => setMetaTodosPeso(e.target.value)}
                    className="input mt-1 block w-36"
                    data-testid="meta-todos-peso"
                  />
                </label>
                <button
                  type="button"
                  className="btn-primary"
                  data-testid="meta-todos-submit"
                  disabled={saveMetaTodos.isPending || (metaTodosCargas === "" && metaTodosPeso === "")}
                  onClick={() => saveMetaTodos.mutate()}
                >
                  {saveMetaTodos.isPending ? "Atribuindo…" : "Atribuir para todos"}
                </button>
              </div>
              {saveMetaTodos.isError && <div className="mt-3"><ErrorBox error={saveMetaTodos.error} /></div>}
              {bulkSuccess && <p className="mt-3 text-sm text-green-700" role="status">{bulkSuccess}</p>}
            </Card>
          )}

          {/* ── Dados (abaixo) — meta editável inline ── */}
          {saveMeta.isError && <div className="mb-3"><ErrorBox error={saveMeta.error} /></div>}
          <Table
            columns={[
              { key: "regiao", label: "Região" },
              { key: "realizado", label: "Realizado", render: (v) => num(v as number) },
              {
                key: "meta_cargas", label: "Meta", render: (v, row) =>
                  canEditMeta ? (
                    <input
                      type="number"
                      min={0}
                      defaultValue={v == null ? "" : String(v)}
                      className="input h-8 w-24 py-1 text-sm"
                      data-testid="meta-input"
                      onBlur={(e) => {
                        const val = e.target.value === "" ? null : Number(e.target.value);
                        if (String(v ?? "") !== String(val ?? "")) {
                          saveMeta.mutate({
                            regiao: String(row.regiao),
                            meta_cargas: val,
                            meta_peso: row.meta_peso == null ? null : Number(row.meta_peso),
                          });
                        }
                      }}
                      onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                    />
                  ) : (v == null ? "—" : num(v as number)),
              },
              {
                key: "meta_peso", label: "Meta (t)", render: (v, row) =>
                  canEditMeta ? (
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      defaultValue={v == null ? "" : String(v)}
                      className="input h-8 w-24 py-1 text-sm"
                      data-testid="meta-peso-input"
                      onBlur={(e) => {
                        const val = e.target.value === "" ? null : Number(e.target.value);
                        if (String(v ?? "") !== String(val ?? "")) {
                          saveMeta.mutate({
                            regiao: String(row.regiao),
                            meta_cargas: row.meta_cargas == null ? null : Number(row.meta_cargas),
                            meta_peso: val,
                          });
                        }
                      }}
                      onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                    />
                  ) : (v == null ? "—" : num(v as number)),
              },
              { key: "toneladas_disponiveis", label: "Disponível (t)", render: (v) => num(v as number) },
              { key: "toneladas_agendadas", label: "Agendado (t)", render: (v) => num(v as number) },
              { key: "toneladas_realizadas", label: "Realizado (t)", render: (v) => num(v as number) },
              { key: "meta_diaria_toneladas", label: "Meta diária (t)", render: (v) => v == null ? "—" : num(v as number) },
              { key: "toneladas_agendadas_hoje", label: "Agenda hoje (t)", render: (v) => num(v as number) },
              { key: "ritmo_dia", label: "Ritmo do dia", render: (v) => pct(v as number | null) },
              { key: "pct_atendida", label: "% atendida", render: (v) => <AttainmentBar frac={v as number | null} /> },
              { key: "pct_ritmo", label: "% ritmo", render: (v) => pct(v as number | null) },
              { key: "ritmo_status", label: "Ritmo", render: (v) => <RitmoBadge status={v as RitmoStatus} /> },
              { key: "veiculos_faltantes_40t", label: "Veículos (40 t)", render: (v) => (v == null ? "—" : num(v as number)) },
            ]}
            rows={rows as unknown as Record<string, unknown>[]}
          />
          <p className="mt-3 text-xs text-slate-400">
            {canEditMeta
              ? "Edite a coluna Meta e saia do campo (Tab/Enter) para salvar. % do ritmo compara o realizado com o esperado até hoje."
              : "Somente comercial/torre/gestor editam a meta. % do ritmo compara o realizado com o esperado até hoje."}
          </p>
        </>
      )}
    </div>
  );
}
